<template>
	<div>
		<!-- Submenu -->
		<policy-submenu />

		<!-- Body -->
		<core-layout-content large>
			<!-- Content -->
			<div class="flex wrap xs-12">
				<!-- Timetravel show when different than current state -->
				<div
					class="xs-12 bgWarning p0 mb4 r2"
					v-if="displayedEvent.type != 'current'"
				>
					<div class="flex xs-between xs-middle p4">
						<div class="xs-6">
							<div class="lh125">
								You are currently seeing data from
								<span class="fw7">
									{{
										$t("events_type." + displayedEvent.type) +
											" (" +
											displayedEvent.sublabel +
											")"
									}}
								</span>
								event
							</div>
						</div>
						<div>
							<j-button
								@click="$modal.show('timeline')"
								type="secondary"
								size="xsmall"
								>Change</j-button
							>
							<j-button
								@click="
									displayedEvent = { type: 'current' }
									fetchPolicy()
								"
								type="primary"
								size="xsmall"
								class="ml2"
								>Go back to present</j-button
							>
						</div>
					</div>
				</div>

				<!-- Left  -->
				<div class="xs-12 md-7 p6" v-if="!policyIsFetched">
					<j-spinner size="small" :delay="0" />
				</div>

				<div class="xs-12 md-7 md-pr4" v-if="policyIsFetched != 0">
					<!-- Particulars -->
					<policy-particulars
						:particulars="fetchedPolicy"
						@updated="fetchPolicy"
					/>

					<!-- Product -->
					<product-info :product="computedProduct" class="mt6" />

					<!-- Premium -->
					<berkley-policy-detail-premium-info
						:pricing="computedPricing"
						class="mt6"
					/>

					<!-- Benefit Options -->
					<policy-benefit-options :list="computedBenefitOptions" class="mt6" />

					<!-- Facts -->
					<berkley-policy-detail-facts :facts="computedFacts" class="mt6" />

					<!-- Holder -->
					<main-holder :holder="computedHolder" class="mt6" />

					<!-- Insured Objects -->
					<insured-objects :list="computedInsuredObjects" class="mt6" />
				</div>

				<!-- Right -->
				<div class="xs-12 md-5 md-pl4">
					<j-card class="bg5 p0">
						<div class="bg4 p4 fs5 lh125 fw7 c1">Time travel</div>
						<div class="p4 lh125">
							You can time travel and see all the important events and state of
							the policy at that time.
							<div class="mt2">
								<j-button @click="$modal.show('timeline')" type="tertiary"
									>See Timeline</j-button
								>
							</div>
						</div>
					</j-card>

					<!-- Info Widget -->
					<core-info-card :data="computedInfo" class="mt6" />

					<!-- Policy actions -->
					<policy-actions
						v-if="$store.state.settings.policies.actions.hasActions"
						v-can="'writePolicies'"
						class="mt6"
						:policyId="fetchedPolicy.id"
						:policyNumber="fetchedPolicy.issuerNumber"
						:amount="computedPricing.amount"
						:currencyCode="computedPricing.currencyCode"
						:context="fetchedPolicy"
					/>

					<!-- Info about cancellation -->
					<cancel-policy-info
						class="mt6"
						v-if="fetchedPolicy.status === 'CANCELLED'"
						:cancellationReason="fetchedPolicy.cancellationReason"
					/>

					<!-- Notes -->
					<core-notes-card
						class="mt6"
						notesOf="policy"
						:id="fetchedPolicy.id"
						:list="computedNotes"
						:canWrite="canWritePolicyNotes"
						@note:created="fetchPolicy"
						@note:updated="fetchPolicy"
						@note:deleted="fetchPolicy"
					/>

					<!-- Files -->
					<core-files-card
						class="mt6"
						:id="fetchedPolicy.id"
						filesOf="policy"
						:list="computedAttachments"
						:canWrite="canWriteFiles"
						@file:deleted="fetchPolicy"
						@files:uploaded="fetchPolicy"
					/>

					<!-- Policy Actions -->
					<berkley-policy-detail-actions
						class="mt6"
						:policyId="fetchedPolicy.id"
						:contractCurrency="contractCurrency"
						:hasContractHolder="Boolean(fetchedPolicy.contractHolder)"
						v-if="isCurrent"
						@action:done="fetchPolicy"
					/>
				</div>
			</div>

			<!-- Bottom menu -->
			<div
				class="flex wrap xs-12 xs-right bt1 bc7 pt2 mt8"
				v-can="'writePolicies'"
			>
				<!-- Delete -->
				<j-button
					v-if="isCurrent"
					type="error"
					@click="$modal.show('j-dialog')"
					v-can="'writePolicies'"
					class="ml2"
					action="showDeletePolicyModal"
				>
					<crm-icon name="delete" size="normal" color="c5" class="mr1" />
					{{ $t("delete_policy") }}
				</j-button>
			</div>
		</core-layout-content>

		<!-- Delete Confirmation Modal -->
		<j-dialog
			:title="$t('delete_policy')"
			type="delete"
			@confirm="onDelete(id)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_policy") }}</j-dialog
		>

		<!-- Timeline modal -->
		<core-modal
			name="timeline"
			:headline="$t('timeline')"
			width="400px"
			:pivotX="0"
			:pivotY="0"
			:hasHeader="false"
		>
			<div class="bg5 pb16 p4" style="height:100vh; overflow-y: auto;">
				<policy-timeline :policyId="id" @item:click="travelToDate($event)" />
			</div>
		</core-modal>

		<!-- Loading toast -->
		<j-loading-toast v-if="isLoading" />
	</div>
</template>

<script>
import MainHolder from "../MainHolder"
import ProductInfo from "../ProductInfo"
import PremiumInfo from "../PremiumInfo"
import InsuredObjects from "../InsuredObjects"
import PolicyParticulars from "../PolicyParticulars"
import PolicyBenefitOptions from "../PolicyBenefitOptions"
import BenefitSummary from "../BenefitSummary"

import PolicySubmenu from "../PolicySubmenu"
import JButton from "@/components/JButton/"
import JSpinner from "@/components/JSpinner/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreModal from "@/modules/core/components/CoreModal"
import CancelPolicyInfo from "../CancelPolicyInfo"
import CoreNotesCard from "@/modules/core/components/CoreNotesCard/index.vue"
import JLoadingToast from "@/components/JLoadingToast"
import CoreFilesCard from "@/modules/core/components/CoreFilesCard"
import CoreInfoCard from "@/modules/core/components/CoreInfoCard"
import CrmIcon from "@/components/CrmIcon"
import PolicyActions from "../PolicyActions"
import ExclusionsInfo from "../ExclusionsInfo"
import JCard from "@/components/JCard"
import get from "lodash.get"
import JDialog from "@/components/JDialog/"
import PolicyTimeline from "../PolicyTimeline"
import { gql } from "@/api/utils.js"
import api from "@/api/gql"
import { can } from "@/permissions/"

// MOCKED: CMS
import BerkleyPolicyDetailActions from "./BerkleyPolicyDetailActions"
import BerkleyPolicyDetailFacts from "./BerkleyPolicyDetailFacts"
import BerkleyPolicyDetailPremiumInfo from "./BerkleyPolicyDetailPremiumInfo"

export default {
	components: {
		MainHolder,
		ProductInfo,
		PremiumInfo,
		InsuredObjects,
		PolicyParticulars,
		PolicyBenefitOptions,
		BenefitSummary,

		PolicySubmenu,
		JButton,
		JSpinner,
		CoreLayoutContent,
		CoreModal,
		CoreNotesCard,
		JLoadingToast,
		CoreFilesCard,
		CoreInfoCard,
		CrmIcon,
		CancelPolicyInfo,
		PolicyActions,
		ExclusionsInfo,
		JCard,
		JDialog,
		PolicyTimeline,

		// MOCKED: CMS
		BerkleyPolicyDetailActions,
		BerkleyPolicyDetailFacts,
		BerkleyPolicyDetailPremiumInfo
	},
	props: {
		id: { type: String, required: true }
	},
	data() {
		return {
			displayedEvent: {
				type: "current"
			},

			// This value is being fetched via API
			// We have to know what is contract currency
			// We get it from policy.facts.currency
			// Based on this, policy is fetched in proper currency
			contractCurrency: null,
			policyIsFetched: 0,
			fetchedPolicy: {},
			isLoading: false
		}
	},
	computed: {
		tenantId() {
			return this.$store.state.tenantId
		},
		isCurrent() {
			return this.displayedEvent.type === "current"
		},
		isEditable() {
			return !this.fetchedPolicy.isIssued && this.isCurrent
		},
		computedHolder() {
			return this.fetchedPolicy.contractHolder
				? this.fetchedPolicy.contractHolder
				: {}
		},
		computedInfo() {
			return {
				status: this.fetchedPolicy.status,
				createdAt: this.fetchedPolicy.createdAt,
				createdBy: this.fetchedPolicy.createdBy,
				lastModifiedAt: this.fetchedPolicy.lastModifiedAt,
				lastModifiedBy: this.fetchedPolicy.lastModifiedBy
			}
		},
		computedProduct() {
			return this.fetchedPolicy?.product || {}
		},
		computedPricing() {
			return this.fetchedPolicy?.premium?.convertedPrices?.[0] || {}
		},
		computedNotes() {
			return this.fetchedPolicy.notes ? this.fetchedPolicy.notes : []
		},
		computedAttachments() {
			return this.fetchedPolicy.attachments
				? this.fetchedPolicy.attachments
				: []
		},
		computedInsuredObjects() {
			const insureds = get(this, "fetchedPolicy.contractInsured")
			if (!insureds) return []
			// Filter out only insured objects, ignore individuals
			return insureds.filter(item => item.__typename === "object")
		},
		computedBenefitOptions() {
			const benefitOptions = get(this, "fetchedPolicy.benefitOptions")
			if (!benefitOptions) return []

			const contractCurrency = this.contractCurrency || ""
			return benefitOptions.map(item => ({
				...item,
				// NOTE: doing formatted value frontend side
				formattedValue:
					contractCurrency +
					" " +
					(item?.value?.numberValue || 0)
						.toFixed(2)
						.replace(/\d(?=(\d{3})+\.)/g, "$&,")
			}))
		},

		computedFacts() {
			const factTypesToDisplaySchema = [
				{ label: "Contract Currency", type: "currency", dataType: "string" },
				{
					label: "Country of risk domicile",
					type: "countryOfRiskDomicile",
					dataType: "string"
				},
				{
					label:
						"Receipt and Satisfactory Review of Signed and Dated Completed Proposal Form prior to binding",
					type: "satisfactoryReview",
					dataType: "boolean"
				},
				{
					label:
						"Warranted that all products insured are complied with statutory requirement and other mandatory and / or voluntary industrial / safety standards of importing countries",
					type: "compliedWithRequirement",
					dataType: "boolean"
				},
				{
					label: "Clean Claim/Loss record for the past 5 years",
					type: "cleanClaimLossRecord",
					dataType: "boolean"
				}
			]

			// Return only facts that we want to display
			// Hide facts with references
			return factTypesToDisplaySchema.map(factToDisplay => {
				return {
					...factToDisplay,
					value: this?.fetchedPolicy?.facts?.find(
						fact => fact.type === factToDisplay.type
					)?.value[`${factToDisplay.dataType}Value`]
				}
			})
		},

		canWritePolicyNotes() {
			return can("writePolicyNotes") && this.isCurrent
		},
		canWriteFiles() {
			return can("writeFiles") && this.isCurrent
		}
	},

	methods: {
		travelToDate(item) {
			this.$modal.hide("timeline")
			this.displayedEvent = item
			this.fetchPolicy(item.timestamp)
		},

		async fetchContractCurrency() {
			const query = gql`
				query fetchContractCurrency(
					$skip: Int
					$limit: Int
					$sort: sortInput
					$where: policyWhereInput
					$asOf: DateTimeOffset
				) {
					policies(
						skip: $skip
						limit: $limit
						sort: $sort
						where: $where
						asOf: $asOf
					) {
						totalCount
						list {
							facts {
								id
								type
								value {
									stringValue
								}
							}
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					where: { id: this.id }
				}
			})
			this.isLoading = false

			// Check errors
			if (!res?.data?.policies?.list) {
				this.$store.dispatch("showError", this.$t("errors.general"))
				return Promise.resolve(false)
			}

			// If it doesn't exist, redirect
			if (!res?.data?.policies?.list?.length) {
				this.$router.push({ name: "PoliciesList" })
				return Promise.resolve(false)
			}

			// Assign
			this.contractCurrency = res?.data?.policies?.list?.[0]?.facts?.find(
				fact => fact.type === "currency"
			)?.value?.stringValue

			return Promise.resolve(true)
		},

		async fetchPolicy(asOf) {
			const query = gql`
				fragment Value on scalarValue {
					booleanValue
					dateValue
					stringValue
					numberValue
				}

				fragment TreeMapValues on scalarValue {
					...Value
					objectValue {
						key
						value {
							...Value
						}
					}
					arrayValue {
						...Value
						objectValue {
							key
							value {
								...Value
							}
						}
					}
				}

				fragment companiesFields on company {
					id
					__typename
					name
					registrationNumber
					natureOfBusiness
					source
					photoPath
					acceptsMarketing
					contacts {
						id
						type
						value
					}
					addresses {
						fields2 {
							type
							value
						}
						fields
						id
						type
					}
					identities {
						id
						type
						value
					}
					facts {
						id
						type
						value {
							...TreeMapValues
						}
					}
				}

				fragment objectsFields on object {
					__typename
					id
					name
					internalCode
					photoPath
					source
					typeId
					contacts {
						id
						type
						value
					}
					addresses {
						fields2 {
							type
							value
						}
						type
						id
						fields
					}
					identities {
						id
						type
						value
					}
					facts {
						id
						type
						value {
							...TreeMapValues
						}
					}
				}

				fragment individualsFields on individual {
					__typename
					id
					name
					photoPath
					englishFirstName
					englishLastName
					chineseFirstName
					chineseLastName
					dateOfBirth
					gender
					occupation
					maritalStatus
					employmentStatus
					salutation
					preferredCommunicationChannel
					preferredLanguage
					countryOfResidency
					incomeRange {
						exclusiveMaximum
						exclusiveMinimum
						minimum
						maximum
					}
					acceptsMarketing
					source
					internalCode
					contacts {
						id
						type
						value
					}
					identities {
						id
						type
						value
					}
					addresses {
						fields2 {
							type
							value
						}
						id
						fields
						type
					}
					facts {
						id
						type
						value {
							...TreeMapValues
						}
					}
				}

				fragment pricing on pricing {
					amount
					currencyCode
					formattedPrice
					originalPrice
					formattedOriginalPrice
					appliedTaxes {
						code
						name
						ratio
						flat
						formattedFlat
						order
						formattedOriginalPrice
						originalPrice
					}
					appliedDiscounts {
						id
						name
						code
						ratio
						flat
						formattedFlat
						order
						formattedOriginalPrice
						originalPrice
					}
					loadings {
						id
						codeName {
							code
							name
						}
						ratio
						flat
						order
					}
				}

				query policy(
					$skip: Int
					$limit: Int
					$sort: sortInput
					$where: policyWhereInput
					$asOf: DateTimeOffset
					$currencyOptions: exchangeRateConversionOptionsInput!
				) {
					policies(
						skip: $skip
						limit: $limit
						sort: $sort
						where: $where
						asOf: $asOf
					) {
						totalCount
						list {
							startDate
							endDate
							status
							isIssued
							id
							source
							description
							issueDate
							createdAt
							createdBy {
								username
							}
							lastModifiedAt
							lastModifiedBy {
								username
							}
							issuerNumber
							isPremiumOverriden
							client {
								id
								name
							}
							values {
								key
								value {
									arrayValue {
										stringValue
										numberValue
										booleanValue
										objectValue {
											key
											value {
												stringValue
												numberValue
												booleanValue
												objectValue {
													key
													value {
														numberValue
														stringValue
														booleanValue
														dateValue
													}
												}
											}
										}
									}
								}
							}
							facts {
								id
								type
								value {
									stringValue
									numberValue
									booleanValue
									dateValue
									arrayValue {
										stringValue
									}
								}
							}
							clauses {
								id
								template {
									id
									name
									graphQlQuery
									... on clauseHtml {
										html
									}
								}
								renderedHtmlResult {
									value
								}
								renderParameters {
									contentJsonString
									variablesJsonString
								}
							}
							attachments {
								path
								fileName
								createdAt
							}
							notes {
								id
								title
								content
								createdAt
								createdBy {
									username
									associatedUser {
										name
									}
								}
							}
							holder {
								... on individual {
									__typename
									id
									name
								}
								... on company {
									__typename
									id
									name
								}
							}
							contractHolder {
								... on individual {
									...individualsFields
								}
								... on company {
									...companiesFields
								}
							}
							otherContractHolders {
								... on individual {
									...individualsFields
								}
								... on company {
									...companiesFields
								}
							}

							contractInsured {
								... on individual {
									...individualsFields
								}
								... on company {
									...companiesFields
								}
								... on object {
									...objectsFields
								}
							}
							referrer {
								name
								internalCode
							}
							paymentInfos {
								id
								method
								name
								amount
								comment
								currencyCode
								frequency
							}
							product {
								productId {
									plan
									type
									version
								}
								name
								insurer {
									id
									name
									logoUrls {
										typeA
									}
								}
								benefits(typeIds: [], includeOptionals: true) {
									typeId
									value
								}
							}
							benefitOptions {
								name
								benefitTypeId
								value {
									...TreeMapValues
								}
								formattedValue
							}
							premium {
								...pricing
								convertedPrices(options: $currencyOptions) {
									...pricing
								}
							}
						}
					}
				}
			`

			const currencyOptions = this.contractCurrency
				? { currencyCodes: [this.contractCurrency] }
				: { currencyCodes: [] }

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					where: { id: this.id },
					asOf,
					currencyOptions
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// If it doesn't exist, redirect
			if (!res?.data?.policies?.list?.length)
				this.$router.push({ name: "PoliciesList" })

			// Assign
			this.fetchedPolicy = res?.data?.policies?.list?.[0]
			this.policyIsFetched++
		},

		async init() {
			// 1: If there is no contractCurrency, fetch it first
			this.isLoading = true
			if (!this.contractCurrency) {
				await this.fetchContractCurrency()
			}

			// 2: fetch policy
			await this.fetchPolicy()
			this.isLoading = false
		},

		async onDelete(id) {
			this.isLoading = true
			const res = await api.deletePolicy({ id })
			this.isLoading = false
			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}
			// go back to previous page
			if (res) this.$router.push(`/${this.$store.state.tenantId}/policies/`)
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("policy_deleted")
				}
			})
		}
	},

	created() {
		// Check if we are receiving all props
		// If not, redirect to products listing
		if (!this.id) {
			this.$router.push({ name: "PoliciesList" })
			return
		}

		// Fetch product info
		this.init()
		// this.fetchPolicy()
	}
}
</script>
